<template>
	<div class="flex w-full flex-col gap-6">
		<div class="flex flex-col gap-4">
			<div class="flex items-center gap-1">
				<h3 class="text-lg leading-5 font-semibold text-won-green">
					Card Details
				</h3>
				<LockClosedIcon
					class="size-4 text-won-green"
					aria-hidden="true"
				/>
			</div>

			<div class="payment-form relative flex w-full flex-col gap-4">
				<div class="flex flex-col gap-2">
					<wrapper label="Card Number">
						<div
							id="payment-card-number"
							ref="cardNumberField"
						/>
						<CreditCardIcon
							class="card-input-icon size-5 text-gray-400"
							aria-hidden="true"
						/>
					</wrapper>

					<div class="grid grid-cols-2 gap-2">
						<wrapper>
							<div
								id="payment-card-date"
								ref="cardExpiryField"
							/>
						</wrapper>

						<wrapper>
							<div
								id="payment-card-cvc"
								ref="cardCvcField"
							/>
							<InformationCircleIcon
								class="card-input-icon size-5 text-gray-400"
								aria-hidden="true"
							/>
						</wrapper>
					</div>
				</div>

				<div class="w-full">
					<label
						for="couponCode"
						class="block text-sm font-medium text-gray-900"
					>Coupon Code</label>
					<div
						v-if="!couponApplied"
						class="mt-1 flex gap-2"
					>
						<input
							id="couponCode"
							v-model="couponCode"
							type="text"
							name="couponCode"
							class="block h-9 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-won-green focus:ring-0 focus:outline-hidden"
							placeholder="Enter your coupon code"
						/>
						<won-button
							class="rounded-md px-3"
							:is-disabled="couponCode === ''"
							@click="applyCoupon"
						>
							Apply
						</won-button>
					</div>
					<div class="flex flex-row flex-wrap justify-start gap-x-2">
						<div
							v-if="couponApplied"
							class="mt-1 flex flex-nowrap items-center justify-between rounded-lg bg-[#F2FAFF] px-1.5 py-px"
						>
							<span class="inline-block font-[Inter] text-xs font-normal text-won-blue italic">{{ couponCode }}</span>

							<span
								class="inline-block cursor-pointer pl-1"
								@click="() => resetCouponCode()"
							>
								<img
									class="h-3"
									:src="CloseImage"
								/>
							</span>
						</div>
					</div>
					<p
						v-if="invalidCoupon"
						class="mt-2 text-sm text-won-red"
					>{{ invalidCouponCodeErrorMessage }}</p>
				</div>
				<div
					v-if="!!error"
					class="won-color-error absolute text-xs font-medium"
					style="bottom: -18px; left: 0"
				>
					{{ error }}
				</div>
			</div>
		</div>

		<div class="flex flex-col items-center gap-4">
			<div class="flex w-full flex-col gap-2">
				<won-button
					class="w-full rounded-md"
					@click="handleSubmit"
				>
					{{ loading ? 'Processing...' : 'Submit Payment' }}

					<template
						v-if="loading"
						#icon
					>
						<loading class="animate-spin" />
					</template>
				</won-button>

				<div class="flex w-full items-center justify-center gap-1">
					<LockClosedIcon
						class="size-4 text-won-secondary"
						aria-hidden="true"
					/>
					<p class="text-xs text-won-secondary">
						All Payment are PCI compliant &amp; SSL Secure
					</p>
				</div>
			</div>

			<stripe-icon />
		</div>
	</div>
</template>

<script>
import { computed, ref } from 'vue';
import { LockClosedIcon } from '@heroicons/vue/20/solid';
import { CreditCardIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';

import Wrapper from '../form-item-wrapper.vue';
import WonButton from '../../won-button.vue';

import Loading from '../../../icons/loading.vue';
import StripeIcon from '../../../icons/stripe.vue';

import { setAuthState } from '../../../composables/auth';
import { useNotifications } from '../../../store/notifications.store';
import { useSubscriptionPlanStore } from '../../../store/subscription.store';
import { subscribe, getPaymentIntent, validateCoupon } from '../../../api/subscription';

import { appInDevMode } from '../../../utils/helpers';
import { ensureStripeLoaded } from '../../../utils/stripe';

const CloseImage = '/img/close.png';

export default {
	components: {
		LockClosedIcon,
		Wrapper,
		CreditCardIcon,
		InformationCircleIcon,
		WonButton,
		Loading,
		StripeIcon,
	},

	emits: ['payment-form:success'],

	setup() {
		const loading = ref(false);
		const error = ref(null);
		const subscriptionStore = useSubscriptionPlanStore();
		const notificationStore = useNotifications();

		// Read directly off the store (it's reactive) instead of storeToRefs: the store
		// exposes a plain `stripeKey` that is undefined when VITE_STRIPE_KEY is unset
		// (e.g. in CI), which makes storeToRefs() throw.
		const selectedOption = computed(() => subscriptionStore.selectedOption);
		const isStripeLoaded = computed(() => subscriptionStore.isStripeLoaded);
		const stripe = computed(() => subscriptionStore.stripe);

		return {
			loading,
			error,
			selectedOption,
			stripe,
			isStripeLoaded,
			notificationStore,
			subscriptionStore,
			CloseImage,
		};
	},

	data: () => ({
		payment3Dsecure: false,
		cardNumber: null,
		cardExpire: null,
		cardCvc: null,
		couponCode: '',
		couponApplied: false,
		invalidCoupon: false,
		invalidCouponCodeErrorMessage: '',

		elementsOptions: {
			classes: {
				base: 'payment-input',
			},
		},
	}),

	async mounted() {
		// Wait for Stripe.js to load before creating the card Elements. Otherwise the
		// fields never mount when the checkout opens with a plan pre-selected (e.g. the
		// first-application paywall) and Stripe.js wasn't loaded earlier in the session (EN-661).
		await ensureStripeLoaded();

		// EN-1136: the paywall can close while Stripe.js loads. Vue clears template refs on
		// unmount, so a missing ref means there is nothing left to mount the fields into.
		const { cardNumberField, cardExpiryField, cardCvcField } = this.$refs;
		if (cardNumberField == null || cardExpiryField == null || cardCvcField == null) {
			return;
		}

		if (! this.isStripeLoaded) {
			this.subscriptionStore.initiateStripe();
		}
		this.creteElements();
		this.addListeners();

		// window.dataLayer = window.dataLayer || [];
		// window.dataLayer.push({ ecommerce: null });
		// window.dataLayer.push({
		//     event: "checkout",
		//     ecommerce: {
		//         checkout: {
		//         actionField: { step: 1, option: "View Billing Page" },
		//         products: [
		//             {
		//                 name: 'Premium price',
		//                 id: 1,
		//                 price: "19.99", // Must be a string. 
		//                 brand: "WonsultingAI",
		//                 category: "Subscription",
		//                 variant: "Monthly",
		//                 quantity: 1,
		//             },
		//         ],
		//         },
		//     },
		// });
	},

	beforeUnmount() {
		this.cardNumber?.destroy();
		this.cardExpire?.destroy();
		this.cardCvc?.destroy();
	},

	methods: {
		creteElements() {
			const elements = this.stripe.elements({
				appearance: {
					theme: 'stripe',
				},
			});

			this.cardNumber = elements.create('cardNumber', {
				...this.elementsOptions,
				placeholder: 'Card Number',
			});
			this.cardNumber.mount(this.$refs.cardNumberField);

			this.cardExpire = elements.create('cardExpiry', {
				...this.elementsOptions,
			});
			this.cardExpire.mount(this.$refs.cardExpiryField);

			this.cardCvc = elements.create('cardCvc', {
				...this.elementsOptions,
				placeholder: 'CVV/CVC',
			});
			this.cardCvc.mount(this.$refs.cardCvcField);
		},

		addListeners() {
			const elements = [this.cardNumber, this.cardExpire, this.cardCvc];

			for (const element of elements) {
				element.on('change', this.handleStripeError);
			}
		},

		handleStripeError(event) {
			const message = event.error ? event.error.message : '';
			this.error = message;
		},

		async handleSubmit() {
			const { getUser } = setAuthState();
			// Captured up front: the success handler clears the store's selection before tracking.
			const selectedAmount = this.selectedOption?.amount;
			const purchasedOption = this.selectedOption;
			this.loading = true;

			try {
				const tokenResult = await getPaymentIntent();

				const result = await this.stripe.confirmCardSetup(tokenResult?.data?.client_secret, {
					payment_method: {
						card: this.cardNumber,
						billing_details: {
							email: getUser()?.email,
						},
					},
				});

				if (result?.error) {
					this.notificationStore.notify(result?.error?.message, 'error');
					return;
				}

				const getUTMParamsFromLocalStorage = () => {
					const utmData = {};
					const utmParams = ['utm_medium', 'utm_source', 'utm_campaign', 'utm_content', 'utm_term'];

					utmParams.forEach((param) => {
						utmData[param] = localStorage.getItem(param);
					});

					return utmData;
				};

				const subscribeToPlan = await subscribe(
					this.selectedOption.default_price,
					result?.setupIntent?.payment_method,
					this.couponCode,
					getUTMParamsFromLocalStorage()
				);

				if (subscribeToPlan?.data?.success) {
					if (subscribeToPlan?.data?.user) {
						const { setUserData } = setAuthState();
						setUserData(subscribeToPlan.data.user);
					}

					this.resetCouponCode();

					this.$emit('payment-form:success');

					// Safely call trackAnalytics with defensive checks
					const transactionId = subscribeToPlan?.data?.data?.id || this.selectedOption?.default_price;
					const transactionAmount = subscribeToPlan?.data?.data?.amount || selectedAmount / 100;
                    
					if (transactionId && transactionAmount) {
						this.trackAnalytics({
							id: transactionId,
							amount: transactionAmount,
							option: purchasedOption,
						});
					}
				}

				if (subscribeToPlan?.data?.error) {
					this.notificationStore.notify(subscribeToPlan?.data?.error ?? 'Something went wrong', 'error');
				}

			} catch (error) {
				if (error?.response?.data?.status === 'requires_action') {
					this.payment3Dsecure = true;
					this.handleCardResult(error.response.data.payment_intent_client_secret);
					return;
				} 
				console.log('error', error);
				this.notificationStore.notify(error?.response?.data?.message ?? 'Something went wrong', 'error');
			} finally {
				if (!this.payment3Dsecure) {
					this.loading = false;
				}
			}
		},
        
		async applyCoupon() {
			this.resetCouponCode();
            
			if (this.couponCode === '') {
				return;
			}
            
			const results = await validateCoupon(this.couponCode);
            
			if (results?.data?.valid) {
				this.couponApplied = true;
				this.subscriptionStore.setCouponCode(results?.data?.coupon, this.selectedOption?.amount);
			} else {
				this.invalidCouponCodeErrorMessage = `Invalid Coupon Code: ${this.couponCode}`;
				this.invalidCoupon = true;
			}
		},
		resetCouponCode() {
			this.invalidCoupon = false;
			this.couponApplied = false;
			this.subscriptionStore.setCouponCode(null);
		},
		trackAnalytics({ amount, option = this.selectedOption }) {
			if (!appInDevMode() && !window.location.hostname.includes('staging')) {
				const { getUser } = setAuthState();

				// Only a Refersion-referred buyer is reported to Refersion; GA4 gets every purchase.
				if (localStorage.getItem('rfsn_v4_id')) {
					const rfsn = {
						cart: getUser()?.stripe_id,
						id: localStorage.getItem('rfsn_v4_id'),
						url: window.location.href,
						aid: localStorage.getItem('rfsn_v4_aid'),
						cs: localStorage.getItem('rfsn_v4_cs')
					};

					// `r` is the Refersion analytics global injected by their script tag.
					/* global r */
					if (typeof r !== 'undefined' && r && r.sendCheckoutEvent) {
						r.sendCheckoutEvent(rfsn?.cart, rfsn?.id, rfsn?.url, rfsn?.aid, rfsn?.cs);
					}
				}

				window.dataLayer = window.dataLayer || [];

				if (window.dataLayer) { 
					// window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
					// window.dataLayer.push({
					//     ecommerce: {
					//     purchase: {
					//         actionField: {
					//             id: transcationId, // Transaction ID. Required for purchases and refunds.
					//             affiliation: "app.wonsulting.com",
					//             revenue: amount, //  get data from stripe
					//             tax: "0.00", // Set to 0 if not applicable. String.
					//             shipping: "0.00", // Set to 0 if not applicable. String
					//             coupon: "", // Can be an empty string if not applicable.
					//         },
					//         products: [
					//         {
					//             name: "Premium Subscription", // Name or ID is required.
					//             id: this.selectedOption.id, // Can be any number as a string. Since there's only one product, it doesn't really matter.
					//             price: amount,
					//             brand: "WonsultingAI",
					//             category: "Subscription",
					//             variant: "Monthly",
					//             quantity: 1,
					//             coupon: "", // Optional fields may be omitted or set to empty string.
					//         },
					//         ],
					//     },
					//     },
					// });

					window.dataLayer.push({
						event: 'purchase',
						productId: option?.id,
						userId: getUser()?.id,
						productCategory: 'Premium',
						productName: option?.default_price ?? 'premium_price', 
						productPrice: Number(amount).toFixed(2),
					});
				}
			}
		},

		async handleCardResult(payment_intent_client_secret) {
			this.loading = true;  
			// Handle the card action required by 3D Secure
			try {
				const confirmPaymentResult = await this.stripe.confirmCardPayment(payment_intent_client_secret, {
					payment_method: {
						card: this.cardNumber,
					},
				});
               
				if (confirmPaymentResult?.error) {
					this.notificationStore.notify(confirmPaymentResult?.error?.message, 'error');
					return;
				}
				// If subscription is successfully active, track and emit success
				if (confirmPaymentResult?.paymentIntent?.status === 'succeeded') {
					// Safely call trackAnalytics with defensive checks
					const transactionId = confirmPaymentResult?.paymentIntent?.id;
					const transactionAmount = confirmPaymentResult?.paymentIntent?.amount
						? confirmPaymentResult.paymentIntent.amount / 100
						: this.selectedOption?.amount / 100;
                    
					if (transactionId && transactionAmount) {
						this.trackAnalytics({
							id: transactionId,
							amount: transactionAmount,
						});
					}
                    
					this.resetCouponCode();
					this.$emit('payment-form:success');
				} 
			} catch {
				// If any error occurs during the process, notify the user
				this.notificationStore.notify('An error occurred during payment confirmation.', 'error');
			} finally {
				setTimeout(() => this.subscriptionStore.refresh(), 2000);
				this.loading = false;
				this.payment3Dsecure = false;
			}
		}
	},
};
</script>

<style scoped>
.card-input-icon {
    position: absolute;
    right: 12px;
    bottom: 8px;
}

.payment-form .payment-input {
    margin-top: 4px;
    padding: 8px 12px;
    border-color: #d1d5db;
    border-radius: 6px;
}
</style>
